import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, VALUES } from "@/lib/site";
import PageHero from "@/components/PageHero";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="Built on craft. Grown through trust."
        subtitle="Asset Group Pte Ltd is a Fiji-based construction company delivering residential, commercial and civil works with discipline and care."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Our Story</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A1A36]">
              A Fijian builder you can rely on.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed">
            <p>
              From our base on MSC Road in Wailoku, Tamavua, we partner with homeowners, businesses
              and developers across Suva and beyond. Our work spans foundation pours, structural
              walls, drainage and civil infrastructure — the unseen and the unforgettable.
            </p>
            <p>
              We believe construction is about more than buildings. It&apos;s about the families that
              live in them, the businesses that operate from them, and the communities that grow
              around them. That belief drives everything we do.
            </p>
            <p>
              Led by {COMPANY.contactPerson}, our team brings practical know-how and a relentless
              focus on doing the work right — the first time.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F8FAFC] py-20 sm:py-28" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Our Promise</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A1A36] max-w-2xl">
            Build. Grow. Prosper.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {VALUES.map((v) => (
              <div key={v.num} className="bg-white border border-[#E2E8F0] p-8" data-testid={`about-value-${v.num}`}>
                <div className="text-[10px] tracking-[0.3em] font-mono text-[#D4AF37]">{v.num} / 03</div>
                <h3 className="font-display text-2xl font-bold mt-2 text-[#0A1A36]">{v.title}.</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Our Commitments</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A1A36]">
              The standards we hold ourselves to.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Every project — large or small — is delivered to the same uncompromising baseline.
            </p>
          </div>
          <ul className="grid gap-4">
            {[
              "Quality construction with durable, climate-suited materials.",
              "Professional conduct on every site — every day.",
              "Reliability you can plan around — clear timelines, honest updates.",
              "Helping clients build strong assets for the future.",
              "Safety-first culture across every crew.",
              "Local expertise grounded in Fiji's terrain and conditions.",
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3 border-l-2 border-[#D4AF37] pl-4 py-2" data-testid={`about-commitment-${idx}`}>
                <CheckCircle2 className="h-5 w-5 text-[#0A1A36] mt-0.5" />
                <span className="text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1A36] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-6">
          <h3 className="font-display text-2xl sm:text-3xl font-bold">
            Ready to start your project with us?
          </h3>
          <div className="flex gap-3">
            <Link to="/quote" data-testid="about-cta-quote">
              <Button className="rounded-none h-12 px-7 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-cta-contact">
              <Button variant="outline" className="rounded-none h-12 px-7 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
